/**
 * Live transcription (frontend §1).
 *
 * Newest at the bottom, doctor and patient visually separated, and the
 * in-progress utterance shown greyed beneath the finalised lines so the doctor
 * can see the recognizer is keeping up.
 *
 * The autoscroll only follows when the doctor is already at the bottom. A
 * transcript that yanks itself down while someone is reading back what was said
 * two minutes ago is worse than one that does not move.
 */

import { Mic, MicOff, Radio } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { TranscriptSegment, TranscriptState } from "@/types/copilot";

const STATE_LABEL: Record<TranscriptState, { text: string; className: string }> = {
  listening: { text: "Listening", className: "text-success" },
  processing: { text: "Processing", className: "text-info" },
  paused: { text: "Paused", className: "text-muted-foreground" },
  unavailable: { text: "Unavailable", className: "text-warning" },
  idle: { text: "Not started", className: "text-muted-foreground" },
};

export function TranscriptSection({
  segments,
  interim,
  state,
  supported,
  enabled,
  onToggle,
}: {
  segments: TranscriptSegment[];
  interim: string;
  state: TranscriptState;
  supported: boolean;
  /** A call is live. The microphone is not offered at any other time. */
  enabled: boolean;
  onToggle: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !pinnedRef.current) return;
    node.scrollTop = node.scrollHeight;
  }, [segments, interim]);

  const onScroll = () => {
    const node = scrollRef.current;
    if (!node) return;
    // A small tolerance, so a couple of pixels of momentum does not unpin it.
    pinnedRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 40;
  };

  const label = STATE_LABEL[state];
  const listening = state === "listening";

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn("inline-flex items-center gap-1.5 text-xs font-medium", label.className)}
        >
          {listening ? (
            <Radio className="size-3.5 animate-pulse" aria-hidden />
          ) : (
            <span className="size-1.5 rounded-full bg-current" aria-hidden />
          )}
          {label.text}
        </span>

        {supported && enabled ? (
          <Button
            type="button"
            variant={listening ? "secondary" : "outline"}
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={onToggle}
            aria-pressed={listening}
          >
            {listening ? <MicOff className="size-3.5" /> : <Mic className="size-3.5" />}
            {listening ? "Pause" : "Transcribe"}
          </Button>
        ) : null}
      </div>

      {supported && !enabled && (
        <p className="text-[0.7rem] leading-relaxed text-muted-foreground">
          Transcription runs only while the consultation is live. Your microphone is not in use.
        </p>
      )}

      {!supported && (
        <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
          This browser cannot transcribe audio. The consultation is unaffected — everything else in
          the copilot still works, and you can add notes by hand.
        </p>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-label="Consultation transcript"
        className="max-h-64 space-y-2 overflow-y-auto rounded-lg bg-muted/60 p-3"
      >
        {segments.length === 0 && !interim ? (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {!supported
              ? "No transcript has been captured for this consultation."
              : state === "listening"
                ? "Listening. The consultation will appear here as it is spoken."
                : enabled
                  ? "Transcription is starting…"
                  : "No transcript has been captured for this consultation."}
          </p>
        ) : (
          segments.map((segment) => <Line key={segment.id} segment={segment} />)
        )}

        {interim && (
          <p className="text-xs leading-relaxed text-muted-foreground italic">{interim}…</p>
        )}
      </div>
    </div>
  );
}

function Line({ segment }: { segment: TranscriptSegment }) {
  const isDoctor = segment.speaker === "doctor";
  const isPatient = segment.speaker === "patient";

  return (
    <div className={cn("flex flex-col gap-0.5", isDoctor && "items-end text-right")}>
      <span
        className={cn(
          "text-[0.65rem] font-semibold tracking-wide uppercase",
          isDoctor ? "text-primary" : isPatient ? "text-rose" : "text-muted-foreground",
        )}
      >
        {isDoctor ? "You" : isPatient ? "Patient" : "Speaker"}
      </span>
      <p
        className={cn(
          "max-w-[92%] rounded-lg px-2.5 py-1.5 text-xs leading-relaxed",
          isDoctor ? "bg-primary/8 text-foreground" : "bg-card text-foreground",
          segment.state === "interim" && "opacity-70",
        )}
      >
        {segment.text}
      </p>
    </div>
  );
}
